import { useState } from 'react'
import recertificationIcon from 'assets/svgs/icon_ls_recertification.svg'
import expiredIcon from 'assets/svgs/icon_ls_expired.svg'
import { FormattedMessage } from 'react-intl'
import ModulesList from 'components/EVLevel/ModulesList'
import { Box, CircularProgress, Paper, Typography, Stack, Button } from '@mui/material'
import { IEVTrainingLevel, ITrainingLevelDetails, IEVApiResponse } from 'interfaces'
import { formatDate } from 'utils/formatDate'
import { useReSelectRecertificationModuleMutation } from 'store/api'
import { useAppSelector } from 'store'
import { selectUserIsImpersonate } from 'store/slices'
import { LightTooltip } from 'routes/Dashboard/style'

type Props = {
  selectedEVTrainingLevel: IEVTrainingLevel
  selectedEvLevelUUID: string
  evLevelData: ITrainingLevelDetails
  onClosePortalModal: () => void
  onMutationSuccess: () => void
  isManagerView: boolean
  passingScore: number
  showRecertificationExtraMessage: boolean
  recertificationMaxAttempts: number
  showRecertificationMaxAttemptsMessage: boolean
  userUUID: string
}

export default function Recertification({
  selectedEVTrainingLevel,
  selectedEvLevelUUID,
  evLevelData,
  onClosePortalModal,
  onMutationSuccess,
  isManagerView,
  showRecertificationExtraMessage,
  recertificationMaxAttempts,
  showRecertificationMaxAttemptsMessage,
  passingScore,
  userUUID,
}: Props) {
  const { isExpired, expiryDate, name } = selectedEVTrainingLevel
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const isImpersonate = useAppSelector(selectUserIsImpersonate)

  const [
    reSelectRecertificationModule,
    { isLoading: isReSelecting, error: reSelectError },
  ] = useReSelectRecertificationModuleMutation()

  const reSelectErrorResponse = reSelectError as IEVApiResponse | undefined
  const reSelectErrorCode = reSelectErrorResponse?.data?.errors?.[0]?.code
  const hasCannotPerformActionError =
    reSelectErrorCode === 'NO_ACTION_WHILE_IMPERSONATING'

  const showReSelectError =
    evLevelData.allowRecertificationModeSelection &&
    !evLevelData.canSelectRecertificationMode &&
    !evLevelData.canReSelectRecertificationMode

  const showReSelectButton =
    evLevelData.canReSelectRecertificationMode || showReSelectError

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: isExpired ? '#9E1B32' : '#5B788F',
        p: 1,
        pt: 2,
      }}
    >
      <Stack direction="row" justifyContent="space-between" mb={2} color="white">
        <Box display="flex">
          <Box pl={1.5} pr={3} flexShrink={0}>
            <Box display="flex" justifyContent="center" mb={1}>
              <img
                src={isExpired ? expiredIcon : recertificationIcon}
                width={40}
                height={40}
                alt="recertification"
              />
            </Box>

            <Typography align="center">
              {expiryDate && !isExpired ? (
                <FormattedMessage id="evLevel.expires" />
              ) : null}
              {isExpired ? <FormattedMessage id="evLevel.expired" /> : null}
            </Typography>
            {expiryDate && (
              <Typography align="center">{formatDate(expiryDate as string)}</Typography>
            )}
          </Box>

          <Box>
            <Typography style={{ fontWeight: 'bold' }}>
              <FormattedMessage
                id={
                  isExpired
                    ? 'evLevel.certificationHasExpired'
                    : 'evLevel.certificationIsDueToExpire'
                }
                values={{ name }}
              />
            </Typography>
            {showRecertificationMaxAttemptsMessage && (
              <Box style={{ marginTop: '10px' }}>
                <p style={{ margin: '0px', fontWeight: 'bold' }}>
                  <FormattedMessage
                    id={'evLevel.certificationAttempts'}
                    values={{ number_attempts: recertificationMaxAttempts }}
                  />
                </p>
                <p style={{ margin: '0px' }}>
                  <FormattedMessage
                    id={'evLevel.certificationFail'}
                    values={{ passing_score: passingScore }}
                  />
                </p>
              </Box>
            )}

            <Box style={{ paddingRight: '5%', paddingBottom: '10px' }}>
              {showRecertificationExtraMessage && (
                <>
                  <br />
                  <Typography style={{ paddingBottom: '5px' }}>
                    <FormattedMessage id={'evLevel.certificationIsDueToExpireTitle1'} />
                  </Typography>
                  <Typography style={{ paddingBottom: '5px' }}>
                    <FormattedMessage id={'evLevel.certificationIsDueToExpireTitle2'} />
                  </Typography>
                  <Typography>
                    <FormattedMessage id={'evLevel.certificationIsDueToExpireTitle3'} />
                  </Typography>
                </>
              )}
            </Box>
            {evLevelData.recertificationModules &&
              evLevelData.recertificationModules?.length > 1 && (
                <Typography my={2}>
                  <FormattedMessage id={'evLevel.recertificationPathways'} />
                </Typography>
              )}
          </Box>
        </Box>
        {showReSelectButton && (
          <Box display="flex" alignItems="center" flexShrink={0} pl={1.5} pr={2.5}>
            <LightTooltip
              title={
                hasCannotPerformActionError || isImpersonate ? (
                  <Box>
                    <p>
                      <FormattedMessage id={'evLevel.cannotPerformAction'} />
                    </p>
                  </Box>
                ) : showReSelectError ? (
                  <Box>
                    <p>
                      <FormattedMessage id={'evLevel.reselectRequired'} />
                    </p>
                    <p>
                      <FormattedMessage id={'evLevel.reselectRequiredContact'} />
                    </p>
                  </Box>
                ) : (
                  ''
                )
              }
              arrow
              open={hasCannotPerformActionError || isTooltipOpen}
              onOpen={() => setIsTooltipOpen(true)}
              onClose={() => setIsTooltipOpen(false)}
              enterTouchDelay={0}
              leaveTouchDelay={3000}
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  disabled={
                    isImpersonate ||
                    isReSelecting ||
                    showReSelectError ||
                    hasCannotPerformActionError
                  }
                  onClick={() =>
                    reSelectRecertificationModule({ userUUID })
                      .unwrap()
                      .then(onMutationSuccess)
                      .catch(() => {})
                  }
                  sx={{
                    whiteSpace: 'nowrap',
                    minWidth: 100,
                    color: 'white',
                    borderColor: 'white',
                    '&.Mui-disabled': {
                      color: 'rgba(255,255,255, 0.4)',
                      borderColor: 'rgba(255,255,255, 0.4)',
                    },
                  }}
                >
                  {isReSelecting ? (
                    <CircularProgress size={18} />
                  ) : (
                    <FormattedMessage id="evLevel.reSelect" />
                  )}
                </Button>
              </span>
            </LightTooltip>
          </Box>
        )}
      </Stack>
      {evLevelData.recertificationModules ? (
        <ModulesList
          evLevelUUID={selectedEvLevelUUID}
          modules={evLevelData.recertificationModules}
          onClosePortalModal={onClosePortalModal}
          onMutationSuccess={onMutationSuccess}
          isManagerView={isManagerView}
          showSelectButton={evLevelData.canSelectRecertificationMode}
          userUUID={userUUID}
        />
      ) : null}
    </Paper>
  )
}
